import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { computed, effect, inject, Injectable, Signal, signal, untracked } from '@angular/core';
import { ToastService } from '@app/shared/services/shared/toast.service';
import {
  CreateUserRequestBody,
  UpdateUserDetailsRequestBody,
  UserDTO
} from '@app/shared/types/models/user/user-dto.model';
import {
  AccountLanguageCode,
  accountLanguageToLocale,
  OwnUserProfile,
  parseAccountLanguage,
  UserProfileCompany
} from '@app/shared/types/models/user/user-profile.model';
import { User } from '@app/shared/types/models/user/user.model';
import { DataStore } from '@app/store/data-store';
import { IdsCollection } from '@app/store/ids-collection';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { catchError, defer, finalize, first, map, Observable, of, Subscription, tap, throwError } from 'rxjs';
import { ActiveCompanyService } from './active-company.service';
import { ApiErrorService } from './api-error.service';
import { AuthService } from './auth.service';
import { DataService } from './data-service';
import { UserProfileFactory } from './factories/user-profile.factory';
import { UserFactory } from './factories/user.factory';
import { UserProfileHttpService } from './http/user-profile-http.service';
import { LanguageService } from './shared/language.service';
import { LoaderService } from './shared/loader.service';

export interface OwnProfileLoadState {
  loading: boolean;
  loaded: boolean;
  error?: 'forbidden' | 'not-found' | 'unavailable' | 'unknown';
}

const INITIAL_OWN_PROFILE_STATE: OwnProfileLoadState = { loading: false, loaded: false };

/**
 * UsersDataService - User entities, company user lists, and related HTTP mutations.
 *
 * Loads company users when {@link ActiveCompanyService.activeCompanyId} is set, and upserts
 * {@link AuthService.currentUser} into the user store when it changes (constructor `effects`), replacing
 * `UserSignedIn` / `ActiveCompanySet` notification handling.
 *
 * Scope: Global (`providedIn: 'root'`).
 *
 * Usage: Company users views, modals, and user pickers.
 *
 * Architecture:
 * - {@link UsersDataService}: Store and HTTP
 * - {@link ActiveCompanyService}: Company scope for company user lists
 * - {@link AuthService}: Source of the signed-in user record in the store
 */
@Injectable({ providedIn: 'root' })
export class UsersDataService extends DataService<UserDTO, User> {
  private readonly users: DataStore<User> = new DataStore<User>();
  private readonly companyUsers: DataStore<IdsCollection> = new DataStore<IdsCollection>();

  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly loaderService: LoaderService = inject(LoaderService);
  private readonly toastService: ToastService = inject(ToastService);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly userFactory: UserFactory = inject(UserFactory);
  private readonly activeCompanyService: ActiveCompanyService = inject(ActiveCompanyService);
  private readonly authService: AuthService = inject(AuthService);
  private readonly userProfileHttpService: UserProfileHttpService = inject(UserProfileHttpService);
  private readonly userProfileFactory: UserProfileFactory = inject(UserProfileFactory);
  private readonly languageService: LanguageService = inject(LanguageService);
  private readonly apiErrorService: ApiErrorService = inject(ApiErrorService);

  // Own profile: a small per-session projection, not a second full-user cache. See UsersDataService docs above.
  private readonly ownProfileCompanies = signal<UserProfileCompany[]>([]);
  private readonly accountLanguage = signal<AccountLanguageCode | undefined>(undefined);
  private readonly ownProfileState = signal<OwnProfileLoadState>(INITIAL_OWN_PROFILE_STATE);
  private readonly preferencesSaving = signal(false);
  private ownProfileSessionGeneration: number | undefined;
  private ownProfileRequest?: Subscription;
  /** Increments on a successful PUT or a reconciling GET; an in-flight profile GET skips applying a stale preference. */
  private preferencesRevision = 0;

  public constructor() {
    super();
    // Registered first so a session-generation change resets own-profile/users state before the
    // currentUser-upsert effect below (or any other effect) can repopulate it from the new session.
    effect(() => {
      const generation = this.authService.sessionGeneration();
      untracked(() => this.resetOwnSessionState(generation));
    });
    effect(() => {
      const companyId = this.activeCompanyService.activeCompanyId();
      if (!companyId) {
        return;
      }
      untracked(() => {
        this.loadCompanyUsers(companyId);
      });
    });
    effect(() => {
      const user = this.authService.currentUser();
      if (!user) {
        return;
      }
      untracked(() => {
        this.users.upsert(user);
      });
    });
  }

  public getById(userId: string): Signal<User | undefined> {
    return computed(() => this.users.get(userId)());
  }

  public getOwnProfileCompanies(): Signal<UserProfileCompany[]> {
    return this.ownProfileCompanies.asReadonly();
  }

  public getAccountLanguage(): Signal<AccountLanguageCode | undefined> {
    return this.accountLanguage.asReadonly();
  }

  public getOwnProfileState(): Signal<OwnProfileLoadState> {
    return this.ownProfileState.asReadonly();
  }

  public isPreferencesSaving(): Signal<boolean> {
    return this.preferencesSaving.asReadonly();
  }

  /** Loads the own-profile aggregate once per session; its `preferences` also seeds the account language, so no separate GET is issued. */
  public loadOwnProfile(): void {
    const actor = this.authService.currentUser();
    if (!actor) {
      return;
    }
    const generation = this.authService.sessionGeneration();
    this.resetOwnSessionState(generation);
    const state = this.ownProfileState();
    if (state.loading || state.loaded) {
      return;
    }
    this.fetchOwnProfile(generation, actor.id);
  }

  /** Explicit retry/refresh; bypasses the "already loaded" dedup guard in {@link loadOwnProfile}. */
  public refreshOwnProfile(): void {
    const actor = this.authService.currentUser();
    if (!actor) {
      return;
    }
    this.fetchOwnProfile(this.authService.sessionGeneration(), actor.id);
  }

  /**
   * PUT language: serializes writes (rejects a concurrent call), is a no-op for an unchanged value,
   * and keeps the previous value/draft on error. On an ambiguous failure it reconciles via a fresh
   * GET rather than assuming the write did or didn't apply.
   */
  public updateAccountLanguage(language: AccountLanguageCode): Observable<AccountLanguageCode> {
    const actor = this.authService.currentUser();
    const generation = this.authService.sessionGeneration();
    return defer(() => {
      if (
        !actor ||
        generation !== this.authService.sessionGeneration() ||
        actor.id !== this.authService.currentUser()?.id
      ) {
        return throwError(() => new Error('Session changed before the preference save started.'));
      }
      if (this.preferencesSaving()) {
        return throwError(() => new Error('A preference save is already in progress.'));
      }
      if (this.accountLanguage() === language) {
        return of(language);
      }
      this.preferencesSaving.set(true);
      return this.userProfileHttpService.updatePreferences({ language }).pipe(
        map(dto => parseAccountLanguage(dto.language)),
        catchError((error: unknown) => {
          // Also reached by a malformed-but-successful PUT response (parseAccountLanguage above throws):
          // reconcile via a fresh GET exactly as for an HTTP error, rather than trusting the bad payload.
          if (generation === this.authService.sessionGeneration()) {
            this.reconcilePreferencesAfterFailedPut(generation);
          }
          return throwError(() => error);
        }),
        map(saved => {
          // The session changed while the PUT was in flight: the write itself may have applied, but this
          // caller's session is gone, so surface it as an error instead of a stale success.
          if (generation !== this.authService.sessionGeneration()) {
            throw new Error('Session changed before the preference save completed.');
          }
          this.preferencesRevision++;
          this.accountLanguage.set(saved);
          // A successful account-language save always wins over an earlier local 12-language override.
          this.languageService.applyExplicitAccountLanguage(accountLanguageToLocale(saved));
          return saved;
        }),
        finalize(() => {
          if (generation === this.authService.sessionGeneration()) {
            this.preferencesSaving.set(false);
          }
        })
      );
    });
  }

  private fetchOwnProfile(generation: number, actorId: string): void {
    this.ownProfileRequest?.unsubscribe();
    this.ownProfileState.set({ loading: true, loaded: false });
    const expectedProfileRevision = this.authService.profileRevision();
    const expectedPreferencesRevision = this.preferencesRevision;

    this.ownProfileRequest = this.userProfileHttpService
      .getMyProfile()
      .pipe(first())
      .subscribe({
        next: dto => {
          if (generation !== this.authService.sessionGeneration()) {
            return;
          }

          let profile: OwnUserProfile;
          try {
            profile = this.userProfileFactory.produce(dto);
          } catch {
            this.ownProfileState.set({ loading: false, loaded: false, error: 'unknown' });
            return;
          }
          if (profile.id !== actorId) {
            this.ownProfileState.set({ loading: false, loaded: false, error: 'unknown' });
            return;
          }

          this.ownProfileCompanies.set(profile.companies);
          if (this.preferencesRevision === expectedPreferencesRevision) {
            this.accountLanguage.set(profile.language);
            this.languageService.applyAccountLanguage(accountLanguageToLocale(profile.language));
          }
          this.authService.applyProfileFields(profile.mergeFields, actorId, generation, expectedProfileRevision);
          this.ownProfileState.set({ loading: false, loaded: true });
        },
        error: (error: unknown) => {
          if (generation !== this.authService.sessionGeneration()) {
            return;
          }
          this.ownProfileState.set({ loading: false, loaded: false, error: this.classifyError(error) });
        }
      });
  }

  private reconcilePreferencesAfterFailedPut(generation: number): void {
    // Snapshot the revision now: if a later save succeeds before this slow GET lands, this reconciliation
    // must not overwrite it with the stale value it was sent to confirm.
    const expectedRevision = this.preferencesRevision;

    this.userProfileHttpService
      .getPreferences()
      .pipe(first())
      .subscribe({
        next: dto => {
          if (generation !== this.authService.sessionGeneration()) {
            return;
          }
          if (this.preferencesRevision !== expectedRevision || this.preferencesSaving()) {
            return;
          }
          try {
            const language = parseAccountLanguage(dto.language);
            this.preferencesRevision++;
            this.accountLanguage.set(language);
            this.languageService.applyAccountLanguage(accountLanguageToLocale(language));
          } catch {
            // Contract violation on reconciliation: keep the last known-good value; the UI may retry the save.
          }
        },
        // Reconciliation failing leaves the previous value and saving state untouched, so the user can
        // simply retry the save; letting this reach the global error handler would be unrelated noise.
        error: () => {}
      });
  }

  private classifyError(error: unknown): OwnProfileLoadState['error'] {
    if (error instanceof HttpErrorResponse) {
      if (error.status === 403) {
        return 'forbidden';
      }
      if (error.status === 404) {
        // Older deployed APIs do not expose this route. A missing account has a business error code.
        return 'unavailable';
      }
      if (error.status === 400 && this.apiErrorService.parse(error)?.errorCode === 'UserIdNotFound') {
        return 'not-found';
      }
      if (error.status === 0 || error.status >= 500) {
        return 'unavailable';
      }
    }
    return 'unknown';
  }

  /** Session boundary (logout, or login — including relogin on the same account): resets own-profile state and the shared user/company caches. */
  private resetOwnSessionState(generation: number): void {
    if (this.ownProfileSessionGeneration === generation) {
      return;
    }
    // The very first run (construction) has nothing to repopulate: the dedicated activeCompanyId/currentUser
    // effects below already handle the initial load, so re-triggering here would fire a duplicate fetch.
    const isFirstReset = this.ownProfileSessionGeneration === undefined;
    this.ownProfileRequest?.unsubscribe();
    this.ownProfileRequest = undefined;
    this.ownProfileSessionGeneration = generation;
    this.ownProfileCompanies.set([]);
    this.accountLanguage.set(undefined);
    this.ownProfileState.set(INITIAL_OWN_PROFILE_STATE);
    this.preferencesSaving.set(false);
    this.preferencesRevision = 0;
    this.users.reset();
    this.companyUsers.reset();

    if (isFirstReset) {
      return;
    }

    // Deterministic repopulation: don't rely on constructor-effect ordering (the currentUser-upsert
    // effect, or a picker that never calls loadCompanyUsers itself) to restore what this reset just wiped.
    const currentUser = this.authService.currentUser();
    if (currentUser) {
      this.users.upsert(currentUser);
    }
    const activeCompanyId = this.activeCompanyService.activeCompanyId();
    if (currentUser && activeCompanyId) {
      this.loadCompanyUsers(activeCompanyId);
    }
  }

  public getCompanyUsers(companyId: string): Signal<User[] | undefined> {
    if (!companyId) {
      return signal(undefined);
    }

    return computed(
      () =>
        this.companyUsers
          .get(companyId)()
          ?.ids?.map(id => this.users.get(id)()) || []
    );
  }

  public loadCompanyUsers(companyId: string): void {
    if (this.companyUsers.hasDataForId(companyId)) {
      return;
    }
    this.fetchCompanyUsers(companyId);
  }

  public upsertLocalData(dto: UserDTO): Signal<User> {
    return dto ? this.users.upsert(this.userFactory.produce(dto)) : signal(undefined);
  }

  private fetchCompanyUsers(companyId: string): void {
    this.loaderService.startLoading();
    const generation = this.authService.sessionGeneration();

    this.httpClient
      .get<UserDTO[]>(`${environment.APIEndPoint}companies/${companyId}/users/all`)
      .pipe(first())
      .subscribe({
        next: (users: UserDTO[]) => {
          // A late response landing after a session change must not recreate this entry: the new
          // session's own fetch would then be skipped by the `hasDataForId` dedup guard.
          if (generation === this.authService.sessionGeneration()) {
            this.users.upsertMany(users.map(itm => this.userFactory.produce(itm)));

            this.companyUsers.upsert({
              id: companyId,
              ids: users.map(itm => itm.id)
            });
          }

          this.loaderService.stopLoading();
        },
        error: () => this.loaderService.stopLoading(),
        complete: () => this.loaderService.stopLoading()
      });
  }

  public updateUserDetails(companyId: string, userId: string, request: UpdateUserDetailsRequestBody): Observable<User> {
    const url = `${environment.APIEndPoint}companies/${companyId}/users/${userId}`;
    this.loaderService.startLoading();
    const generation = this.authService.sessionGeneration();

    return this.httpClient.put<UserDTO>(url, request).pipe(
      map(dto => {
        // A late response landing after a session change must not repopulate the (reset) store.
        if (generation !== this.authService.sessionGeneration()) {
          return this.userFactory.produce(dto);
        }
        const user = this.upsertLocalData(dto)();
        this.toastService.success(
          this.translateService.instant('toasts.user-details-successfully-saved', {
            value: `${user.firstName} ${user.lastName}`
          })
        );
        return user;
      }),
      catchError((error: unknown) => throwError(() => error)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public createUser(companyId: string, request: CreateUserRequestBody): Observable<User> {
    const url = `${environment.APIEndPoint}companies/${companyId}/users`;
    this.loaderService.startLoading();
    const generation = this.authService.sessionGeneration();

    return this.httpClient.post<UserDTO>(url, request).pipe(
      map(userDTO => {
        // A late response landing after a session change must not repopulate the (reset) store.
        if (generation !== this.authService.sessionGeneration()) {
          return this.userFactory.produce(userDTO);
        }
        const user = this.upsertLocalData(userDTO)();

        this.companyUsers.upsert({
          id: companyId,
          ids: (this.companyUsers.get(companyId)()?.ids ?? []).concat(userDTO.id)
        });

        this.toastService.success(
          this.translateService.instant('toasts.user-successfully-created', {
            value: `${userDTO.firstName} ${userDTO.lastName}`
          })
        );

        return user;
      }),
      catchError((error: unknown) => throwError(() => error)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public deleteUser(companyId: string, user: User): Observable<void> {
    this.loaderService.startLoading();

    return this.httpClient.delete<void>(`${environment.APIEndPoint}companies/${companyId}/users/${user.id}`).pipe(
      tap(() => {
        this.users.delete(user.id);
        const companyUserIds = this.companyUsers.get(companyId)();
        if (companyUserIds?.ids?.length) {
          this.companyUsers.upsert({
            id: companyId,
            ids: companyUserIds.ids.filter(applicationUserId => applicationUserId !== user.id)
          });
        }
        this.toastService.success(
          this.translateService.instant('toasts.user-successfully-deleted', {
            value: user.fullName
          })
        );
      }),
      catchError((error: unknown) => throwError(() => error)),
      finalize(() => this.loaderService.stopLoading())
    );
  }

  public lockUser(companyId: string, user: User): Observable<User> {
    this.loaderService.startLoading();
    const generation = this.authService.sessionGeneration();

    return this.httpClient
      .put<UserDTO>(`${environment.APIEndPoint}companies/${companyId}/users/${user.id}/lock`, {})
      .pipe(
        map(dto => {
          // A late response landing after a session change must not repopulate the (reset) store.
          if (generation !== this.authService.sessionGeneration()) {
            return this.userFactory.produce(dto);
          }
          const updated = this.upsertLocalData(dto)();
          this.toastService.success(
            this.translateService.instant('toasts.user-successfully-locked', { value: user.fullName })
          );
          return updated;
        }),
        catchError((error: unknown) => throwError(() => error)),
        finalize(() => this.loaderService.stopLoading())
      );
  }

  public unlockUser(companyId: string, user: User): Observable<User> {
    this.loaderService.startLoading();
    const generation = this.authService.sessionGeneration();

    return this.httpClient
      .put<UserDTO>(`${environment.APIEndPoint}companies/${companyId}/users/${user.id}/unlock`, {})
      .pipe(
        map(dto => {
          // A late response landing after a session change must not repopulate the (reset) store.
          if (generation !== this.authService.sessionGeneration()) {
            return this.userFactory.produce(dto);
          }
          const updated = this.upsertLocalData(dto)();
          this.toastService.success(
            this.translateService.instant('toasts.user-successfully-unlocked', { value: user.fullName })
          );
          return updated;
        }),
        catchError((error: unknown) => throwError(() => error)),
        finalize(() => this.loaderService.stopLoading())
      );
  }

  public resendActivationLink(user: User): Observable<void> {
    this.loaderService.startLoading();

    return this.httpClient
      .post<void>(`${environment.APIEndPoint}auth/resend-account-activation-link`, { userId: user.id })
      .pipe(
        tap(() => {
          this.toastService.success(this.translateService.instant('toasts.user-successfully-resend-activation-link'));
        }),
        catchError((error: unknown) => throwError(() => error)),
        finalize(() => this.loaderService.stopLoading())
      );
  }
}
